import React, { createContext, useCallback, useContext, useState } from 'react';

import { buildDimensionReportTree } from '../buildDimensionReportTree';

const BudgetByDimensionReportTableContext = createContext();

export const useBudgetByDimensionReportTableContext = () => {
    const context = useContext(BudgetByDimensionReportTableContext);

    if (!context) {
        throw new Error('useBudgetByDimensionReportTableContext must be used within a BudgetByDimensionReportTableProvider');
    }

    return context;
};

/**
 * Table context for Budgets by Venue / Budgets by Event on-screen report.
 *
 * The data shape returned by the backend service is a flat array of rows in
 * each sheet (summary / detailed / detailed_comparison).  Rows are keyed by
 * a 'department' field; dimension columns carry dimension IDs as keys.
 *
 * expanded state: { [rowKey]: boolean }
 * rowKey = row['department'] value for summary, or row['item'] for detailed.
 */
export const BudgetByDimensionReportTableProvider = ({
    criteria,
    report,
    dimensions,
    loading,
    isVikEnabled,
    dimension,
    showComments = false,
    editableComments = false,
    comments,
    allComments,
    userType,
    children,
}) => {
    const [expanded, setExpanded] = useState({});

    const toggleExpansion = useCallback((rowKey) => {
        setExpanded((prev) => ({
            ...prev,
            // Default (undefined) is "expanded" (true), so first click collapses (false)
            [rowKey]: !(prev[rowKey] ?? true),
        }));
    }, []);

    // Tree-mode hierarchy nodes default to collapsed (like Budgets by
    // Department), so undefined means collapsed and the first click expands.
    const toggleNodeExpansion = useCallback((nodeKey) => {
        setExpanded((prev) => ({
            ...prev,
            [nodeKey]: !prev[nodeKey],
        }));
    }, []);

    const toggleAllExpansion = useCallback(
        (expand = true) => {
            if (!report) {
                return;
            }

            const options = {};

            // Expand / collapse summary rows (keyed by department label)
            const summarySheet = report.summary || [];
            summarySheet.forEach((row) => {
                if (row && row.department) {
                    options[row.department] = expand;
                }
            });

            // Expand / collapse the detailed sheet: every hierarchy node in
            // tree mode, or the section headers (groups-styled rows) in the
            // legacy flat mode.
            const detailedSheet = report.detailed || [];
            const detailedTree = buildDimensionReportTree(detailedSheet);
            if (detailedTree) {
                detailedTree.nodeKeys.forEach((key) => {
                    options[`tree:detailed:${key}`] = expand;
                });
            } else {
                detailedSheet.forEach((row) => {
                    // Skip indexed-array spacer rows
                    if (!row || Array.isArray(row)) return;
                    const label = row.department || row.type;
                    const isSectionHeader =
                        label &&
                        !row.venue &&
                        !row.costcenter &&
                        !row.project &&
                        !row.item &&
                        (row.total === null || row.total === undefined || row.total === '');
                    if (isSectionHeader) {
                        options[`detailed:${label}`] = expand;
                    }
                });
            }

            // Expand / collapse the detailed_comparison sheet, same dual mode
            const compSheet = report.detailed_comparison || [];
            const comparisonTree = buildDimensionReportTree(compSheet);
            if (comparisonTree) {
                comparisonTree.nodeKeys.forEach((key) => {
                    options[`tree:comparison:${key}`] = expand;
                });
            } else {
                compSheet.forEach((row) => {
                    // Skip indexed-array spacer rows
                    if (!row || Array.isArray(row)) return;
                    const label = row.department || row.type;
                    const isSectionHeader = label && !row.costcenter && !row.project && !row.item;
                    if (isSectionHeader) {
                        options[`comparison:${label}`] = expand;
                    }
                });
            }

            setExpanded(options);
        },
        [report],
    );

    const isOptionIncluded = useCallback(
        (optionValue) => {
            return criteria && !!criteria[`include_${optionValue}`];
        },
        [criteria],
    );

    const value = {
        loading,
        report,
        dimensions: dimensions || [],
        criteria,
        expanded,
        isVikEnabled,
        dimension: dimension || 'venue',
        showComments,
        editableComments,
        comments,
        allComments: allComments || {},
        userType,
        toggleExpansion,
        toggleNodeExpansion,
        toggleAllExpansion,
        isOptionIncluded,
    };

    return <BudgetByDimensionReportTableContext.Provider value={value}>{children}</BudgetByDimensionReportTableContext.Provider>;
};

export default BudgetByDimensionReportTableContext;
